import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { SITE_ORIGIN, useSEO } from '@/hooks/useSEO';
import { useFormSubmit } from '@/hooks/useFormSubmit';

const CONTACT_ENDPOINT = 'https://readdy.ai/api/form/dailpf3p14a1h3mr76gg';
const MESSAGE_LIMIT = 500;

const CHANNELS = [
  {
    icon: 'ri-mail-line',
    label: '通用咨询',
    value: 'hello@5kanliao.com',
    desc: '了解网站、内容合作或任何其他问题，都可以从这里开始。',
  },
  {
    icon: 'ri-briefcase-line',
    label: '商务合作',
    value: 'bd@5kanliao.com',
    desc: '品牌合作与内容共建请注明合作方向，我们会在两个工作日内回复。',
  },
  {
    icon: 'ri-error-warning-line',
    label: '内容异议',
    value: 'feedback@5kanliao.com',
    desc: '若认为某条速览存在信息偏差，请附上条目名称与依据，我们会优先核实。',
  },
];

const CONTACT_JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ContactPage',
      name: '联系我们 - 5看料网',
      url: `${SITE_ORIGIN}/contact`,
      inLanguage: 'zh-CN',
      description: '通过邮箱或留言表单联系5看料网的内容与商务团队。',
    },
    {
      '@type': 'Organization',
      name: '5看料网',
      url: `${SITE_ORIGIN}/`,
      email: 'hello@5kanliao.com',
      contactPoint: [
        { '@type': 'ContactPoint', contactType: 'customer support', email: 'hello@5kanliao.com' },
        { '@type': 'ContactPoint', contactType: 'sales', email: 'bd@5kanliao.com' },
      ],
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: '联系我们', item: `${SITE_ORIGIN}/contact` },
      ],
    },
  ],
};

export default function Contact() {
  const { status, message, submit } = useFormSubmit(
    CONTACT_ENDPOINT,
    '留言已提交，我们会在两个工作日内回复你。',
  );
  const [messageText, setMessageText] = useState('');

  useSEO({
    title: '联系我们 | 5看料网速览快看',
    description:
      '联系5看料网的内容与商务团队。可通过邮箱或留言表单提交内容异议、商务合作与选题建议，我们会在两个工作日内回复。',
    keywords: '5看料网联系方式,内容异议反馈,商务合作,留言表单',
    path: '/contact',
    jsonLd: CONTACT_JSON_LD,
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(event.currentTarget);
  };

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="w-full px-4 md:px-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-body text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
            <span className="text-foreground-800">联系我们</span>
          </nav>
          <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
            联系我们
          </h1>
          <p className="mt-4 max-w-2xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是内容异议、合作意向，还是你想推荐我们整理的选题，都欢迎告诉我们。留言后我们会在两个工作日内回复。
          </p>
        </div>
      </section>

      <section className="w-full py-12 md:py-16">
        <div className="w-full px-4 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
                我们更习惯用邮件沟通，这样每一条反馈都能被记录下来，也方便后续跟进。
              </p>

              <ul className="mt-8 flex flex-col gap-4">
                {CHANNELS.map((channel) => (
                  <li key={channel.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className={`${channel.icon} text-xl`} aria-hidden="true"></i>
                      </span>
                      <span>
                        <span className="block font-heading text-sm font-semibold text-foreground-950">
                          {channel.label}
                        </span>
                        <span className="mt-0.5 block font-body text-sm text-primary-600">{channel.value}</span>
                      </span>
                    </div>
                    <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">{channel.desc}</p>
                  </li>
                ))}
              </ul>

              <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-accent-900">
                  <i className="ri-time-line text-base" aria-hidden="true"></i>
                  回复时间说明
                </h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-foreground-700">
                  工作日 10:00 至 18:00 提交的留言，通常会在当天或次日收到回复；周末与节假日的留言会顺延到下一个工作日处理。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                id="kanliao-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
              >
                <h2 className="font-heading text-xl font-bold text-foreground-950">留言给我们</h2>
                <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">
                  带 <span className="text-primary-600">*</span> 的为必填项。
                </p>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="block font-label text-sm text-foreground-700">
                      你的称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      placeholder="怎么称呼你"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block font-label text-sm text-foreground-700">
                      邮箱地址 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      placeholder="用于接收回复"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="contact-topic" className="block font-label text-sm text-foreground-700">
                    留言类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="内容异议反馈"
                    className="mt-2 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="内容异议反馈">内容异议反馈</option>
                    <option value="商务合作">商务合作</option>
                    <option value="投稿与线索">投稿与线索</option>
                    <option value="网站建议">网站建议</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="mt-5">
                  <label htmlFor="contact-message" className="block font-label text-sm text-foreground-700">
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={MESSAGE_LIMIT}
                    rows={6}
                    value={messageText}
                    onChange={(event) => setMessageText(event.target.value)}
                    placeholder="请尽量说明具体情况，方便我们更快地跟进。"
                    className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 font-body text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  ></textarea>
                  <p className="mt-2 text-right font-body text-xs text-foreground-500">
                    {messageText.length} / {MESSAGE_LIMIT}
                  </p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="alt-contact-field"
                />

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
                  {status === 'submitting' ? '提交中…' : '提交留言'}
                </button>

                {message && (
                  <p
                    role="status"
                    className={`mt-4 font-body text-sm ${
                      status === 'success' ? 'text-secondary-700' : 'text-primary-600'
                    }`}
                  >
                    {message}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}